import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, Loader2, RefreshCw, Upload, CheckCircle2, FileText, Headphones } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useAdmin } from "@/hooks/use-admin";
import { storePostLoginRedirect } from "@/lib/auth-redirect";
import {
  listAssignedConciergeRequests,
  updateConciergeStatus,
  saveConciergeDeliverable,
  createDeliverableUploadUrl,
  deliverConcierge,
} from "@/lib/concierge.functions";

export const Route = createFileRoute("/concierge/agent")({ component: AgentPage });

const STATUSES = ["novo", "em_analise", "em_desenvolvimento", "concluido", "entregue", "cancelado"] as const;
const LABEL: Record<string, string> = {
  novo: "Novo",
  em_analise: "Em análise",
  em_desenvolvimento: "Em desenvolvimento",
  concluido: "Concluído",
  entregue: "Entregue",
  cancelado: "Cancelado",
};

type Row = {
  id: string;
  destination: string;
  start_date: string | null;
  end_date: string | null;
  people: number | null;
  interests: string[] | null;
  transport: string | null;
  accommodation: string | null;
  notes: string | null;
  status: string;
  created_at: string;
  requester_email: string | null;
  requester_name: string | null;
  deliverable_text: string | null;
  deliverable_pdf_url: string | null;
  delivered_at: string | null;
  is_first_free: boolean;
  payment_status: string;
  app_route_link: string | null;
};

function AgentPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isAdmin, isConciergeAgent, loading: roleLoading } = useAdmin();
  const navigate = useNavigate();

  const listFn = useServerFn(listAssignedConciergeRequests);
  const statusFn = useServerFn(updateConciergeStatus);

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      storePostLoginRedirect();
      navigate({ to: "/login" });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const canAccess = isAdmin || isConciergeAgent;

  const refresh = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const r = await listFn();
      setRows(r as unknown as Row[]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    } finally {
      setLoading(false);
    }
  }, [canAccess, listFn]);

  useEffect(() => { if (canAccess) refresh(); }, [canAccess, refresh]);

  async function changeStatus(id: string, status: string) {
    try {
      await statusFn({ data: { id, status: status as typeof STATUSES[number] } });
      toast.success("Estado atualizado");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    }
  }

  if (authLoading || roleLoading) {
    return <main className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></main>;
  }
  if (!canAccess) {
    return (
      <main className="mx-auto max-w-md px-4 py-16 text-center">
        <Headphones className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Sem acesso</h1>
        <p className="mt-2 text-sm text-muted-foreground">Esta área é reservada a colaboradores dos Roteiros Personalizados.</p>
        <Link to="/" className="mt-4 inline-block text-sm text-brand hover:underline">Voltar ao início</Link>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6 flex items-center justify-between gap-3">
        <div>
          <Link to="/" className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3 w-3" /> Início
          </Link>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Headphones className="h-5 w-5 text-brand" /> Os meus roteiros
          </h1>
          <p className="text-sm text-muted-foreground">Pedidos que te foram atribuídos.</p>
        </div>
        <button onClick={refresh}
          className="inline-flex items-center gap-1 rounded-full border border-input px-3 py-1.5 text-xs hover:bg-accent">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Atualizar
        </button>
      </header>

      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase">
            <tr>
              <th className="p-3">Cliente</th>
              <th className="p-3">Destino</th>
              <th className="p-3">Datas</th>
              <th className="p-3">Pax</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Criado</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr className="border-t border-border">
                  <td className="p-3">
                    <div className="font-medium">{r.requester_name || "—"}</div>
                    {r.requester_email && (
                      <a href={`mailto:${r.requester_email}`} className="text-xs text-brand hover:underline">{r.requester_email}</a>
                    )}
                  </td>
                  <td className="p-3 font-medium">{r.destination}</td>
                  <td className="p-3">{r.start_date || "—"} → {r.end_date || "—"}</td>
                  <td className="p-3">{r.people ?? "—"}</td>
                  <td className="p-3">
                    <select value={r.status} onChange={(e) => changeStatus(r.id, e.target.value)}
                      className="rounded-md border border-input bg-background px-2 py-1 text-xs">
                      {STATUSES.map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("pt-PT")}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                      className="rounded-full border border-input px-3 py-1 text-xs hover:bg-accent">
                      {expanded === r.id ? "Fechar" : "Abrir"}
                    </button>
                  </td>
                </tr>
                {expanded === r.id && (
                  <tr className="bg-muted/20">
                    <td colSpan={7} className="p-4 text-sm">
                      <div className="grid grid-cols-2 gap-3">
                        <Info label="Transporte" value={r.transport} />
                        <Info label="Alojamento" value={r.accommodation} />
                        <Info label="Interesses" value={(r.interests ?? []).join(", ") || null} />
                        <Info label="Pagamento" value={r.is_first_free ? "Grátis (1º)" : r.payment_status} />
                      </div>
                      {r.notes && (
                        <div className="mt-3">
                          <div className="text-xs font-semibold uppercase text-muted-foreground">Notas do cliente</div>
                          <div className="whitespace-pre-wrap">{r.notes}</div>
                        </div>
                      )}
                      <hr className="my-4 border-border" />
                      <DeliverableBlock row={r} onSaved={refresh} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Ainda não te foi atribuído nenhum pedido.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}

function Info({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-muted-foreground">{label}</div>
      <div>{value || "—"}</div>
    </div>
  );
}

function DeliverableBlock({ row, onSaved }: { row: Row; onSaved: () => void }) {
  const saveDelivFn = useServerFn(saveConciergeDeliverable);
  const uploadUrlFn = useServerFn(createDeliverableUploadUrl);
  const deliverFn = useServerFn(deliverConcierge);

  const [text, setText] = useState(row.deliverable_text ?? "");
  const [pdfPath, setPdfPath] = useState(row.deliverable_pdf_url ?? "");
  const [appRouteLink, setAppRouteLink] = useState(row.app_route_link ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function save() {
    setBusy("save");
    try {
      await saveDelivFn({ data: { id: row.id, text: text || null, pdfPath: pdfPath || null, appRouteLink: appRouteLink || null } });
      toast.success("Guardado");
      onSaved();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Erro"); }
    finally { setBusy(null); }
  }

  async function uploadPdf(file: File) {
    setBusy("upload");
    try {
      const { path, signedUrl } = await uploadUrlFn({ data: { id: row.id, filename: file.name } });
      const put = await fetch(signedUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type || "application/pdf" } });
      if (!put.ok) throw new Error("Upload falhou");
      setPdfPath(path);
      toast.success("PDF carregado. Não te esqueças de Guardar.");
    } catch (e) { toast.error(e instanceof Error ? e.message : "Erro"); }
    finally { setBusy(null); if (fileRef.current) fileRef.current.value = ""; }
  }

  async function deliver() {
    setBusy("deliver");
    try {
      await deliverFn({ data: { id: row.id } });
      toast.success("Entregue ao cliente");
      onSaved();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Erro"); }
    finally { setBusy(null); }
  }

  const canDeliver = (row.is_first_free || row.payment_status === "pago") && (Boolean(text) || Boolean(pdfPath));

  return (
    <div className="rounded-xl border border-border bg-background p-4">
      <div className="mb-2 text-xs font-bold uppercase text-muted-foreground">Rota final</div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
        placeholder="Escreve a rota (aceita markdown). Podes deixar em branco se anexares só um PDF."
        className="w-full rounded-md border border-input bg-background p-2 text-sm" />
      <div className="mt-3">
        <div className="text-xs font-semibold text-muted-foreground">Link da rota na app</div>
        <input type="text" value={appRouteLink} onChange={(e) => setAppRouteLink(e.target.value)}
          placeholder="https://routify-tour.com/pro/r/..."
          className="mt-1 w-full rounded-md border border-input bg-background px-2 py-1 text-sm" />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input ref={fileRef} type="file" accept="application/pdf"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPdf(f); }}
          className="text-xs" />
        {busy === "upload" && <Loader2 className="h-3 w-3 animate-spin" />}
        <Upload className="h-3 w-3 text-muted-foreground" />
      </div>
      {pdfPath && (
        <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
          <FileText className="h-3 w-3" /> {pdfPath.split("/").pop()}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <button onClick={save} disabled={busy !== null}
          className="rounded-full border border-input px-3 py-1.5 text-xs hover:bg-accent">
          {busy === "save" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Guardar rascunho"}
        </button>
        <button onClick={deliver} disabled={busy !== null || !canDeliver}
          className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
          {busy === "deliver" ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
          {row.delivered_at ? "Reenviar" : "Marcar entregue"}
        </button>
      </div>
      {!row.is_first_free && row.payment_status !== "pago" && (
        <p className="mt-2 text-[11px] text-amber-600">Aguarda o pagamento (o admin trata do preço/link) antes de entregar.</p>
      )}
    </div>
  );
}
